import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle } from "lucide-react";

const NEEDS = ["Noleggio", "Assistenza", "Toner & Consumabili", "Altro"];

export const QuoteModal = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nome: "", telefono: "", richiesta: "Noleggio", messaggio: "" });

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-quote-modal", handler);
    return () => window.removeEventListener("open-quote-modal", handler);
  }, []);

  useEffect(() => {
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
  }, [open]);

  const submit = (e) => {
    e.preventDefault();
    const lines = [
      "Richiesta preventivo dal sito:",
      `Nome: ${form.nome}`,
      form.telefono && `Telefono: ${form.telefono}`,
      `Richiesta: ${form.richiesta}`,
      form.messaggio && `Messaggio: ${form.messaggio}`,
    ]
      .filter(Boolean)
      .join("\n");
    window.open(`https://wa.me/393460868653?text=${encodeURIComponent(lines)}`, "_blank");
    setOpen(false);
  };

  const field = "w-full bg-transparent border-b border-[#C5C2BA] focus:border-[#181817] outline-none py-3 text-[#181817] placeholder:text-[#6E6D69]/60 transition-colors duration-300";
  const label = "font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E6D69]";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="quote-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] bg-[#181817]/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-lenis-prevent
            className="w-full max-w-lg bg-[#FBF9F5] border border-[#E5E2DC] rounded-sm p-8 sm:p-10 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C85A32]">Preventivo gratuito</p>
                <h3 className="mt-2 font-display text-3xl font-light text-[#181817]">Richiedi preventivo</h3>
                <p className="mt-2 text-sm text-[#6E6D69]">Compila e invia: ti arriviamo direttamente su WhatsApp.</p>
              </div>
              <button
                data-testid="quote-modal-close"
                onClick={() => setOpen(false)}
                aria-label="Chiudi"
                className="w-10 h-10 shrink-0 rounded-full border border-[#C5C2BA] flex items-center justify-center text-[#181817] hover:bg-[#181817] hover:text-[#FBF9F5] transition-colors duration-300"
              >
                <X size={16} />
              </button>
            </div>
            <form onSubmit={submit} className="mt-8 flex flex-col gap-6">
              <div>
                <label className={label} htmlFor="quote-nome">Nome e cognome *</label>
                <input
                  id="quote-nome"
                  data-testid="quote-name-input"
                  required
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="Mario Rossi"
                  className={field}
                />
              </div>
              <div>
                <label className={label} htmlFor="quote-telefono">Telefono</label>
                <input
                  id="quote-telefono"
                  data-testid="quote-phone-input"
                  type="tel"
                  value={form.telefono}
                  onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  placeholder="333 123 4567"
                  className={field}
                />
              </div>
              <div>
                <span className={label}>Di cosa hai bisogno?</span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {NEEDS.map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`quote-need-${n.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setForm({ ...form, richiesta: n })}
                      className={`px-4 py-2 rounded-full text-sm border transition-colors duration-300 ${
                        form.richiesta === n
                          ? "bg-[#181817] text-[#FBF9F5] border-[#181817]"
                          : "border-[#C5C2BA] text-[#6E6D69] hover:text-[#181817] hover:border-[#181817]"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={label} htmlFor="quote-messaggio">Messaggio</label>
                <textarea
                  id="quote-messaggio"
                  data-testid="quote-message-input"
                  rows={3}
                  value={form.messaggio}
                  onChange={(e) => setForm({ ...form, messaggio: e.target.value })}
                  placeholder="Es. Vorrei noleggiare una multifunzione A3 a colori per il mio ufficio…"
                  className={`${field} resize-none`}
                />
              </div>
              <button
                data-testid="quote-submit-button"
                type="submit"
                className="mt-2 flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-3.5 rounded-full text-sm font-medium hover:bg-[#1eb856] transition-colors duration-300"
              >
                <MessageCircle size={16} /> Invia su WhatsApp
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
